import store from '@/store/index.js';
import locationFilterConst from '@/consts/locationFilter.const.js';
// import timeUtils from '@/services/common/timeUtils.js';
// import getComparablesModes from '@/consts/getComparablesModes.js';

const transformFilterToSearch = ({ newFilters = null, newCompSortList = null, newBasketSortList = null, newSearchName = null, tabTypeId }) => {
  const previousSearch = store.getters['researchModule/getSelectedSearch'];

  // Fields that might be updated
  const CompSortList = newCompSortList || previousSearch.TabSearch.CompSortList;
  const BasketSortList = newBasketSortList || previousSearch.TabSearch.BasketSortList;
  const SearchName = newSearchName || previousSearch.SearchName;
  let Filter = newFilters || previousSearch.TabSearch.Filter;

  // Handle special cases when changing between searching for freetext and figure
  const filterHasFreeTextObj = Filter.some((a) => a.MetricKey === 'FreeText');
  const filterHasSearchByFreeTextObj = Filter.some((a) => a.MetricKey === 'SearchBy' && a.Values === '02');
  const isFreeTextFilter = filterHasFreeTextObj && filterHasSearchByFreeTextObj;
  const searchZoneFigure = store.getters['comparablesModule/getSearchZoneFigure'](tabTypeId);
  const isRegionFilterButHasFreeTextObject = filterHasFreeTextObj && !filterHasSearchByFreeTextObj && !!searchZoneFigure;

  if (isFreeTextFilter) {
    // Regions are still in the store when you search by freetext after you've searched by figure, so we need to remove the regions by hand
    // (this is also done in pixResearchComparableMap searchArea handler)
    store.dispatch('comparablesModule/setComparableSearchZone', { circle: null, polygon: null, dataLayer: null, selectedTab: tabTypeId });
  }

  if (isRegionFilterButHasFreeTextObject) {
    // The freeText filter is still being saved when you search by figure after you've searched by freetext, so we need to remove freetext by hand
    Filter = Filter.filter((a) => a.MetricKey !== 'FreeText');
  }

  const search = {
    ...previousSearch,
    SearchName,
    TabSearch: {
      Filter,
      CompSortList,
      BasketSortList,
      DistanceUnits: previousSearch.TabSearch.DistanceUnits,
      Circle: null,
      Polygon: null,
      Multipolygon: null,
    },
  };

  return addRegionFilter(search, tabTypeId);
};

const addRegionFilter = (search, tabTypeId) => {
  // Addition of region filter
  const searchZoneFigureType = store.getters['comparablesModule/getSearchZoneFigureType'](tabTypeId);
  const searchZoneFigure = store.getters['comparablesModule/getSearchZoneFigure'](tabTypeId);

  if (searchZoneFigureType === 'circle') {
    search.TabSearch.Circle = searchZoneFigure;
  } else if (searchZoneFigureType === 'polygon') {
    if (searchZoneFigure.length === 1) {
      search.TabSearch.Polygon = searchZoneFigure[0].coordinates;
    } else if (searchZoneFigure.length > 1) {
      search.TabSearch.Multipolygon = searchZoneFigure.map((a) => a.coordinates);
    }
  }

  return search;
};

const saveResearch = (researchConfig) => {
  store.dispatch('researchModule/saveSearch', transformFilterToSearch(researchConfig));
};

const saveResearchAndFetchComparablesWithBasket = async (researchConfig, fetchComparableWithBasketParameter) => {
  saveResearch(researchConfig);

  await store.dispatch('comparablesModule/fetchComparablesWithBasket', fetchComparableWithBasketParameter);
};

const getParsedFilterValue = (filter) => {
  if (!filter) {
    return;
  }
  const parsedFilters = [];

  if ((filter.FieldControlType === 'input' || filter.FieldControlType === 'datePicker') && filter.Value) {
    parsedFilters.push({ MetricKey: filter.InternalField, Values: `${filter.Value}` });
  } else if ((filter.FieldControlType === 'inputRange' || filter.FieldControlType === 'dateRange') && (filter.MinValue || filter.MaxValue)) {
    parsedFilters.push({ MetricKey: filter.InternalField, Values: `${filter.MinValue || 'NULL'};${filter.MaxValue || 'NULL'}` });
  } else if ((filter.FieldControlType === 'select' || filter.FieldControlType === 'radio') && filter.Value?.ValueCode) {
    parsedFilters.push({ MetricKey: filter.InternalField, Values: `${filter.Value.ValueCode}` });
  } else if (filter.FieldControlType === 'multiselect' && filter.Value?.length) {
    const values = filter.Value.map((value) => value.ValueCode);
    parsedFilters.push({ MetricKey: filter.InternalField, Values: values.join(';') });
  } else if (filter.FieldControlType === 'locationSearch') {
    filter.SearchType === null && parsedFilters.push({ MetricKey: 'SearchBy', Values: filter.SearchType });
    filter.SearchType?.ValueCode && parsedFilters.push({ MetricKey: 'SearchBy', Values: filter.SearchType?.ValueCode });
    filter.Location?.fullLabel && parsedFilters.push({ MetricKey: 'Location', Values: filter.Location.fullLabel });
    filter.Polygon?.fullLabel && parsedFilters.push({ MetricKey: 'Polygon', Values: filter.Polygon.fullLabel });
    filter.FreeText && parsedFilters.push({ MetricKey: 'FreeText', Values: filter.FreeText });
    filter.Area && parsedFilters.push({ MetricKey: 'Area', Values: filter.Area });
  }

  if ((filter.HasNotNull && filter.NotNullValue === true) || filter.NotNullValue === false) {
    parsedFilters.push({ MetricKey: `${filter.InternalField}NotNull`, Values: `${filter.NotNullValue}` });
  }

  return parsedFilters;
};

// Returns an array of filters where the original filters are overwritten if there is a filter in the new filters array that has the same MetricKey value
const mergeOldAndNewFilters = (originalFilters, newFilters) => {
  let parsedFilters = [];

  if (originalFilters && newFilters) {
    const parsedFiltersMetricKeys = newFilters.map((parsedFilter) => parsedFilter.MetricKey);
    const filteredOriginalFilters = [...originalFilters].filter((filter) => !parsedFiltersMetricKeys.includes(filter.MetricKey) && ['Location', 'Polygon', 'FreeText'].includes(filter.MetricKey));
    parsedFilters = [...filteredOriginalFilters, ...newFilters];

    const searchByValue = parsedFilters.find(({ MetricKey }) => MetricKey === 'SearchBy')?.Values;

    if (searchByValue) {
      if (searchByValue !== locationFilterConst.ADDRESS) parsedFilters = parsedFilters.filter(({ MetricKey }) => MetricKey !== 'Location');
      if (searchByValue !== locationFilterConst.GEOGRAPHY) parsedFilters = parsedFilters.filter(({ MetricKey }) => MetricKey !== 'Polygon');
      if (searchByValue !== locationFilterConst.FREE_TEXT) parsedFilters = parsedFilters.filter(({ MetricKey }) => MetricKey !== 'FreeText');
    } else {
      parsedFilters = parsedFilters.filter(({ MetricKey }) => MetricKey !== 'SearchBy');
    }
  }

  return parsedFilters;
};

const doInfiniteScrolling = (observedElement, fn, delay = 0) => {
  const handleIntersect = (entries) => {
    let prevRatio = 0;
    setTimeout(() => {
      entries.forEach((entry) => {
        if (entry.intersectionRatio > prevRatio) {
          fn && fn();
        }
        prevRatio = entry.intersectionRatio;
      });
    }, delay);
  };

  const options = {
    root: null,
    rootMargin: '0px',
    threshold: [0.1],
  };
  const observer = new IntersectionObserver(handleIntersect, options);
  observer.observe(observedElement);
};

export default {
  transformFilterToSearch,
  getParsedFilterValue,
  saveResearch,
  saveResearchAndFetchComparablesWithBasket,
  mergeOldAndNewFilters,
  doInfiniteScrolling,
};
